// In-browser face kit (detection, 68 landmarks, 128-d descriptor). No expression/age models are loaded.
let faceapi = null;
let loading = null;
const MODEL_URL = '/models/face-api';

export async function loadFaceKit() {
  if (faceapi) return faceapi;
  if (!loading) {
    loading = (async () => {
      const mod = await import('@vladmandic/face-api');
      const gl = await mod.tf.setBackend('webgl').catch(() => false);
      if (!gl) await mod.tf.setBackend('cpu');
      await mod.tf.ready();
      await Promise.all([
        mod.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        mod.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        mod.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      faceapi = mod;
      return mod;
    })();
  }
  return loading;
}

const opts = (size = 320) => new faceapi.TinyFaceDetectorOptions({ inputSize: size, scoreThreshold: 0.45 });

export async function detectFaces(input, { descriptors = false, size = 320 } = {}) {
  const fa = await loadFaceKit();
  let q = fa.detectAllFaces(input, opts(size)).withFaceLandmarks();
  if (descriptors) q = q.withFaceDescriptors();
  return q;
}

export async function bestDescriptor(input, size = 416) {
  const faces = await detectFaces(input, { descriptors: true, size });
  if (!faces.length) return { count: 0, descriptor: null, landmarks: null };
  const best = faces.reduce((a, b) => (a.detection.box.area > b.detection.box.area ? a : b));
  return { count: faces.length, descriptor: Array.from(best.descriptor), landmarks: best.landmarks };
}

export function distance(a, b) {
  if (!a || !b || a.length !== b.length) return null;
  let s = 0;
  for (let i = 0; i < a.length; i += 1) s += (a[i] - b[i]) ** 2;
  return Math.sqrt(s);
}

const d2 = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);

// Eye aspect ratio (Soukupová & Čech) over both eyes; drops sharply on a blink.
export function eyeAspectRatio(landmarks) {
  const ear = (e) => (d2(e[1], e[5]) + d2(e[2], e[4])) / (2 * d2(e[0], e[3]));
  return (ear(landmarks.getLeftEye()) + ear(landmarks.getRightEye())) / 2;
}

// Head yaw proxy: nose tip horizontal offset relative to the face width (-1 left .. 1 right).
export function yawRatio(landmarks) {
  const jaw = landmarks.getJawOutline();
  const nose = landmarks.getNose()[3];
  const left = jaw[0].x;
  const right = jaw[16].x;
  return ((nose.x - left) / (right - left || 1) - 0.5) * 2;
}

export function frameToJpeg(video, maxW = 640, quality = 0.82) {
  const w = Math.min(maxW, video.videoWidth || maxW);
  const h = Math.round(w * ((video.videoHeight || 480) / (video.videoWidth || 640)));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  c.getContext('2d').drawImage(video, 0, 0, w, h);
  return new Promise((res) => c.toBlob((b) => res({ blob: b, canvas: c }), 'image/jpeg', quality));
}

export function imageToJpeg(img, maxW = 1280, quality = 0.86) {
  const sw = img.naturalWidth || img.width;
  const sh = img.naturalHeight || img.height;
  const w = Math.min(maxW, sw);
  const h = Math.round(w * (sh / sw));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  c.getContext('2d').drawImage(img, 0, 0, w, h);
  return new Promise((res) => c.toBlob((b) => res({ blob: b, canvas: c }), 'image/jpeg', quality));
}

const pause = (ms) => new Promise((r) => setTimeout(r, ms));
const biggest = (faces) => faces.reduce((a, b) => (a.detection.box.area > b.detection.box.area ? a : b));

// Active liveness: two randomised challenges (blink, head turn) checked from live landmarks.
export async function runLiveness(video, { onStep, isCancelled = () => false, timeoutMs = 15000 } = {}) {
  const order = Math.random() < 0.5 ? ['blink', 'turn'] : ['turn', 'blink'];
  const results = [];
  for (const type of order) {
    onStep?.(type, 'active');
    const t0 = performance.now();
    const ears = [];
    const yaws = [];
    let closed = false;
    let yaw0 = null;
    let passed = false;
    while (!passed && performance.now() - t0 < timeoutMs && !isCancelled()) {
      const faces = await detectFaces(video, { size: 224 });
      if (faces.length) {
        const lm = biggest(faces).landmarks;
        if (type === 'blink') {
          const ear = eyeAspectRatio(lm);
          ears.push(ear);
          if (ears.length > 40) ears.shift();
          const open = [...ears].sort((a, b) => a - b)[Math.floor(ears.length * 0.8)];
          if (ears.length >= 5 && ear < open * 0.78) closed = true;
          else if (closed && ear > open * 0.88) passed = true;
        } else {
          const y = yawRatio(lm);
          if (yaw0 === null) {
            yaws.push(y);
            if (yaws.length >= 4) yaw0 = yaws.reduce((a, b) => a + b, 0) / yaws.length;
          } else if (Math.abs(y - yaw0) > 0.28) passed = true;
        }
      }
      if (!passed) await pause(40);
    }
    results.push({ type, passed, ms: Math.round(performance.now() - t0) });
    onStep?.(type, passed ? 'passed' : 'failed');
    if (isCancelled()) break;
  }
  return { passed: results.length === order.length && results.every((r) => r.passed), challenges: results };
}

export function fileToImage(file) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = URL.createObjectURL(file);
  });
}

const VIRTUAL_CAM = /(obs|virtual|manycam|snap camera|xsplit|droidcam|epoccam|camtwist|mmhmm|nvidia broadcast|e2esoft|splitcam|youcam)/i;
export const isVirtualCameraLabel = (label) => VIRTUAL_CAM.test(label || '');
